import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IconArrowUpCircle } from "@touchtech/web-baselayer-ui";

import { TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useSearch } from "@is/hooks";
import { SearchTermType, Section } from "@is/types";
import { convertToSectionHeader } from "@is/utils";

import css from "../ContentSection.module.css";

const useManageContentSection = (section: Section) => {
  const {
    page,
    searchTerms,
    id: sectionId,
    items: contentItems,
    notViewedItems,
    hasNextPage,
  } = section;

  const { t } = useTranslation(TranslationContext.ProductListing);

  const {
    sections,
    updateViewState,
    focusedItemId,
    showSearchAndFooterDrawers,
    firstSectionOffset: offset,
  } = useContext(ViewStateContext).content;

  const { handleSearch, isPending } = useSearch();

  const isFirstSection = useMemo(
    () => sections[0]?.id === sectionId,
    [sectionId, sections]
  );

  const searchTermsWithoutSizesAndLength = useMemo(
    () =>
      searchTerms.filter(
        (term) =>
          !(
            term.type === SearchTermType.Size ||
            term.type === SearchTermType.Length ||
            term.type === SearchTermType.Price
          )
      ),
    [searchTerms]
  );

  const sizesAndLength = useMemo(
    () =>
      searchTerms.filter(
        (term) =>
          term.type === SearchTermType.Size ||
          term.type === SearchTermType.Length
      ),
    [searchTerms]
  );

  const sectionHeaderWithoutSizesAndLength = useMemo(
    () => convertToSectionHeader(searchTermsWithoutSizesAndLength),
    [searchTermsWithoutSizesAndLength]
  );

  const sectionHeaderSizesAndLength = useMemo(
    () => convertToSectionHeader(sizesAndLength),
    [sizesAndLength]
  );

  const noMatchingItems = useMemo(() => !contentItems?.length, [contentItems]);

  const isOnlyOneItem = useMemo(
    () => contentItems?.length === 1,
    [contentItems]
  );

  const isOnlySection = useMemo(
    () => sections[0].id === sectionId && sections.length === 1,
    [sectionId, sections]
  );

  const isLastSection = useMemo(
    () => sections[sections.length - 1].id === sectionId && sections.length > 1,
    [sectionId, sections]
  );

  const handleShowMore = useCallback(() => {
    if (!hasNextPage) {
      return;
    }

    if (notViewedItems.length) {
      updateViewState({
        sections: sections.map((section) => ({
          ...section,
          items:
            sectionId === section.id
              ? [...section.items, ...notViewedItems]
              : section.items,
          notViewedItems:
            sectionId === section.id ? [] : section.notViewedItems,
        })),
        focusedItemId:
          sectionId === section.id ? notViewedItems[0].id : focusedItemId,
      });

      return;
    }

    handleSearch({
      searchTerms,
      sectionId,
      pageNumber: page + 1,
      enableFocusedItem: false,
      showDrawers: false,
    });
  }, [
    updateViewState,
    handleSearch,
    focusedItemId,
    hasNextPage,
    notViewedItems,
    page,
    searchTerms,
    section,
    sectionId,
    sections,
  ]);

  const handleSectionHeaderClick = useCallback(() => {
    const filteredSearchTerms = searchTerms.filter(
      (term) => term.type !== SearchTermType.Scanned
    );

    updateViewState({
      ...(filteredSearchTerms.length && { searchTerms: filteredSearchTerms }),
      showSearchAndFooterDrawers: true,
    });
  }, [searchTerms, updateViewState]);

  const showMorePlaceholder = useMemo(() => {
    if (
      (isLastSection || isOnlySection) &&
      (noMatchingItems || isOnlyOneItem)
    ) {
      return null;
    }

    if (!hasNextPage) {
      return (
        <div className={css.showMoreContainer}>
          <div className={css.showMorePlaceholderContainer}>
            <div className={css.noMatchingText}>
              {t("No more matching items found")}
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className={css.showMoreContainer}>
          <div className={css.showMorePlaceholderContainer}>
            <button className={css.showMoreButton} onClick={handleShowMore}>
              <IconArrowUpCircle />
              {t("show more like this")}
            </button>
          </div>
        </div>
      );
    }
  }, [
    isLastSection,
    isOnlySection,
    noMatchingItems,
    isOnlyOneItem,
    hasNextPage,
    t,
    handleShowMore,
  ]);

  return {
    t,
    searchTermsWithoutSizesAndLength,
    sectionHeaderWithoutSizesAndLength,
    sectionHeaderSizesAndLength,
    noMatchingItems,
    isOnlySection,
    isLastSection,
    showMorePlaceholder,
    contentItems,
    sectionId,
    handleShowMore,
    searchTerms,
    handleSectionHeaderClick,
    showSearchAndFooterDrawers,
    offset: isFirstSection ? offset : 0,
    isPending: isPending && !isLastSection && !isOnlySection,
  };
};

export default useManageContentSection;
